/**
 * §P.1 · La ficha de un pagador.
 *
 * **Es la pantalla de «esta familia llama porque no entiende su recibo»**, y por
 * eso el servidor la sirve entera en una llamada: el pagador, a quién cubre y sus
 * últimos 24 recibos. Quien la abre tiene a alguien al teléfono.
 *
 * De ahí dos decisiones:
 *
 * - **Los recibos primero, con su estado en color.** La pregunta es «¿qué se me ha
 *   cobrado?», no «¿cuál es mi NIF?».
 * - **El aviso de «sin cuenta» va arriba y grande.** Si esta persona llama porque
 *   no ve su recibo en la app, esa es la respuesta, y está en un campo que de otra
 *   forma nadie mira.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { etagDe } from '@/shared/api/concurrencia';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { euros, fecha } from '@/shared/ui/formato';
import {
  sinCuenta,
  type FichaDePagador as Ficha,
} from '@/club/pagadores/tipos';
import { estadoDeCuotaEnEspanol } from '@/club/cuotas/tipos';
import { FormularioDePagador } from '@/club/pagadores/FormularioDePagador';

/** El estado de un recibo, con el color que le corresponde. Y no se tiñe. */
const PASTILLA_DE_RECIBO: Record<string, string> = {
  paid: 'pastilla-active',
  issued: 'pastilla-paused',
  draft: 'pastilla-left',
  failed: 'pastilla-mal',
  void: 'pastilla-left',
};

const ETIQUETA_DE_RECIBO: Record<string, string> = {
  paid: 'Cobrado',
  issued: 'Pendiente',
  draft: 'Borrador',
  failed: 'Impagado',
  void: 'Anulado',
};

export function FichaDePagador({
  club,
  pagadorId,
  puedeEscribir,
  suspendido,
  alCerrar,
  alAbrirAlumno,
}: {
  club: string;
  pagadorId: string;
  puedeEscribir: boolean;
  suspendido: boolean;
  alCerrar: () => void;
  alAbrirAlumno: (alumnoId: string) => void;
}) {
  const [editando, setEditando] = useState(false);

  const ficha = useQuery({
    queryKey: ['pagador', club, pagadorId],
    queryFn: async () => {
      const r = await api.v1
        .clubs({ club })
        .payers({ payerId: pagadorId })
        .get();
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      // El `ETag` es del pagador y es lo que pide el `PATCH`: viaja con la ficha.
      return { ficha: r.data as unknown as Ficha, etag: etagDe(r) };
    },
  });

  return (
    <section className="ficha">
      <header className="titulo-fila">
        <button type="button" className="btn-plano" onClick={alCerrar}>
          ← Pagadores
        </button>
        {puedeEscribir && ficha.data && !editando && (
          <button type="button" onClick={() => setEditando(true)}>
            Editar
          </button>
        )}
      </header>

      {ficha.isPending && <Cargando filas={4} etiqueta="Cargando el pagador" />}

      {ficha.isError && (
        <PanelError
          problema={aProblema(ficha.error)}
          reintentar={() => void ficha.refetch()}
        />
      )}

      {ficha.data && editando && (
        <FormularioDePagador
          club={club}
          pagador={ficha.data.ficha.payer}
          etag={ficha.data.etag}
          suspendido={suspendido}
          alGuardar={() => setEditando(false)}
          alCancelar={() => setEditando(false)}
        />
      )}

      {ficha.data && !editando && (
        <>
          <h1>{ficha.data.ficha.payer.legal_name}</h1>

          {sinCuenta(ficha.data.ficha.payer) && (
            <div className="nota">
              <strong>Esta persona no ve sus recibos en la app.</strong> No
              tiene cuenta vinculada, así que los recibos se emiten y no le
              aparecen en el móvil. Si llama por eso, esta es la razón.
            </div>
          )}

          <dl className="datos">
            <div>
              <dt>NIF</dt>
              <dd>{ficha.data.ficha.payer.tax_id ?? '—'}</dd>
            </div>
            <div>
              <dt>Correo</dt>
              <dd>{ficha.data.ficha.payer.email ?? '—'}</dd>
            </div>
            <div>
              <dt>Teléfono</dt>
              <dd>{ficha.data.ficha.payer.phone ?? '—'}</dd>
            </div>
            <div>
              <dt>Localidad</dt>
              <dd>
                {[
                  ficha.data.ficha.payer.postal_code,
                  ficha.data.ficha.payer.city,
                ]
                  .filter(Boolean)
                  .join(' ') || '—'}
              </dd>
            </div>
          </dl>

          <h2 className="seccion">Recibos</h2>

          {ficha.data.ficha.invoices.length === 0 ? (
            <Vacio
              titulo="Todavía no se le ha emitido ningún recibo"
              explicacion="Aparecerán aquí en cuanto haya una cuota suya en una remesa."
            />
          ) : (
            <div className="tabla-marco">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>Periodo</th>
                    <th>Vencimiento</th>
                    <th className="num">Importe</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {ficha.data.ficha.invoices.map((r) => (
                    <tr key={r.invoice_id}>
                      <td>{r.period ?? '—'}</td>
                      <td className="tenue">{fecha(r.due_date)}</td>
                      {/* Céntimos enteros hasta el formateador. */}
                      <td className="num">{euros(r.total_cents)}</td>
                      <td>
                        <span
                          className={`pastilla ${PASTILLA_DE_RECIBO[r.status] ?? 'pastilla-left'}`}
                        >
                          {ETIQUETA_DE_RECIBO[r.status] ?? r.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h2 className="seccion">A quién cubre</h2>

          {ficha.data.ficha.students.length === 0 ? (
            <Vacio
              titulo="No cubre a nadie todavía"
              explicacion="Se le asigna desde la cuota de un alumno: es la cuota la que dice quién paga."
            />
          ) : (
            <ul className="cubiertos">
              {ficha.data.ficha.students.map((a) => (
                <li key={a.student_id}>
                  <button
                    type="button"
                    className="enlace-fila"
                    onClick={() => alAbrirAlumno(a.student_id)}
                  >
                    {[a.first_name, a.last_name].filter(Boolean).join(' ')}
                  </button>
                  <span className="pastilla pastilla-info">
                    Cuota {estadoDeCuotaEnEspanol(a.status)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
